import React, { useState } from 'react';
import { useAsync } from 'react-use';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  EmptyState,
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { PRList } from '../PRList';
import { RepoResult } from '../../types';

export function EntityComponentPRCard() {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [showDependabot, setShowDependabot] = useState(false);

  const slug = entity.metadata.annotations?.['github.com/project-slug'];

  const { value, loading, error } = useAsync(async (): Promise<RepoResult[]> => {
    if (!slug) return [];
    const baseUrl = await discoveryApi.getBaseUrl('github-pr-dashboard');
    const response = await fetchApi.fetch(
      `${baseUrl}/prs?repos=${encodeURIComponent(slug)}`,
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch PRs: ${response.statusText}`);
    }
    return response.json();
  }, [slug]);

  if (!slug) {
    return (
      <InfoCard title="Pull Requests">
        <EmptyState
          missing="field"
          title="Missing annotation"
          description="Add the 'github.com/project-slug' annotation to this component's catalog-info.yaml to enable the PR dashboard."
        />
      </InfoCard>
    );
  }

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  const allPRs = value?.[0]?.pullRequests ?? [];
  const pullRequests = showDependabot
    ? allPRs
    : allPRs.filter(pr => pr.author !== 'dependabot[bot]');

  const action = (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={showDependabot}
          onChange={e => setShowDependabot(e.target.checked)}
        />
      }
      label="Show Dependabot"
    />
  );

  return (
    <InfoCard title="Pull Requests" action={action}>
      <PRList pullRequests={pullRequests} />
    </InfoCard>
  );
}
